// ============================================================
// utils/estoque.js
//
// Helpers para classificar o status de uma posicao de estoque.
//
// Os 6 estados possiveis sao:
//   - ACIMA_MAX  : qtd > maximo definido (excesso)
//   - OK         : entre o limite saudavel
//   - ATENCAO    : entre min e min * 1.5 (proximo de virar critico)
//   - CRITICO    : qtd > 0 mas abaixo do minimo
//   - ZERADO     : qtd = 0 (so contabilizado se min > 0)
//   - SEM_LIMITE : produto sem minimo definido naquele estoque
//
// Regra-chave: posicoes ZERADAS so contam como problema se houver
// minimo definido. Sem minimo, a posicao zerada simplesmente nao
// aparece (nao polui a tela).
// ============================================================

export const STATUS = {
  ZERADO: 'ZERADO',
  CRITICO: 'CRITICO',
  ATENCAO: 'ATENCAO',
  OK: 'OK',
  ACIMA_MAX: 'ACIMA_MAX',
  SEM_LIMITE: 'SEM_LIMITE',
};

// Multiplicador do limite minimo para acionar o estado ATENCAO.
// Ex.: min=10 -> atencao quando qtd estiver entre 10 e 15.
const FATOR_ATENCAO = 1.5;

/**
 * Classifica uma posicao de estoque com base em qtd / min / max.
 * Numeros sao normalizados para Number; valores invalidos viram 0.
 *
 * @param {number} qtd   Quantidade real no estoque
 * @param {number} min   Quantidade minima desejada (0 = sem minimo)
 * @param {number} max   Quantidade maxima desejada (0 = sem maximo)
 * @returns {string}     Uma das chaves de STATUS
 */
export function calcularStatusEstoque(qtd, min, max) {
  const q = Number(qtd) || 0;
  const mn = Number(min) || 0;
  const mx = Number(max) || 0;

  // Excesso: independente do minimo
  if (mx > 0 && q > mx) return STATUS.ACIMA_MAX;

  // Sem minimo definido: nao classifica como problema
  if (mn <= 0) return STATUS.SEM_LIMITE;

  if (q <= 0) return STATUS.ZERADO;
  if (q < mn) return STATUS.CRITICO;
  if (q < mn * FATOR_ATENCAO) return STATUS.ATENCAO;

  return STATUS.OK;
}

/**
 * Retorna true se o status indica um problema que precisa de atencao
 * (CRITICO, ZERADO ou ACIMA_MAX). ATENCAO nao e considerado "problema",
 * apenas alerta preventivo.
 */
export function statusEhProblema(status) {
  return (
    status === STATUS.CRITICO ||
    status === STATUS.ZERADO ||
    status === STATUS.ACIMA_MAX
  );
}

/**
 * Retorna true se a posicao deve aparecer na expansao do produto.
 * Regra: posicoes com qtd > 0 sempre aparecem; posicoes zeradas so
 * aparecem se tiverem minimo definido (problema real).
 */
export function posicaoDeveAparecer(qtd, min) {
  const q = Number(qtd) || 0;
  const mn = Number(min) || 0;
  if (q > 0) return true;
  return mn > 0; // qtd = 0 e min definido => aparecer
}

/**
 * Label amigavel para exibicao em portugues.
 */
export function labelStatus(status) {
  switch (status) {
    case STATUS.ZERADO:     return 'Zerado';
    case STATUS.CRITICO:    return 'Abaixo do mín';
    case STATUS.ATENCAO:    return 'Em atenção';
    case STATUS.OK:         return 'OK';
    case STATUS.ACIMA_MAX:  return 'Acima do máx';
    case STATUS.SEM_LIMITE: return 'OK';
    default:                return '';
  }
}